import { createFileRoute, type SearchSchemaInput } from '@tanstack/react-router';
import { operatorMeQuery } from '@/services/operator';
import { roleIncludes } from '@/features/team/role-gate';
import { MoneyPage } from '@/features/money/money-page';
import { moneyReadQuery } from '@/features/money/money-read';
import type { MoneyAddress, MoneySearch } from '@/features/money/money-search';
import { mayBeOperator } from '@/features/operator/operator-view';
import { PersonaPage } from '@/features/operator/persona-page';
import { OperatorMoneyPage } from '@/features/operator-money/operator-money-page';

export const Route = createFileRoute('/_authed/money')({
  // `program` opens one campaign (Home's Results card links to the program it shows); the operator's filters too. Links
  // give a `MoneySearch`; the address is passed on unread and the pages read it (`parseMoneySearch`, in their own chunk):
  // a route's validator ships in the entry chunk, which the sign-in page loads (its 150 KB budget).
  validateSearch: (search: MoneySearch & SearchSchemaInput): MoneyAddress => search,
  // Her role first: a role without the Money page shows the role card and requests nothing. The loader never throws.
  // A seat that may run the campaigns also asks whether it does (a client seat never asks), so neither page flashes.
  // While previewing a teammate, the Money read is the preview's (`moneyReadQuery`), the one the page shows.
  loader: async ({ context: { queryClient, team, user } }) => {
    if (!team || !(await roleIncludes(queryClient, team, 'money'))) return;
    await Promise.all([
      queryClient.ensureQueryData(moneyReadQuery(team.id, user.id)).catch(() => null),
      mayBeOperator(team.role) ? queryClient.ensureQueryData(operatorMeQuery(team.id)).catch(() => null) : null,
    ]);
  },
  component: MoneyRoute,
});

/** The operator's Money for someone who runs the campaigns; the client's page, exactly, for everyone else. */
function MoneyRoute() {
  return <PersonaPage operator={OperatorMoney} managed={ClientMoney} />;
}

function OperatorMoney() {
  return <OperatorMoneyPage search={Route.useSearch()} />;
}

function ClientMoney() {
  return <MoneyPage search={Route.useSearch()} />;
}
